import {
	AzureTicketDraft,
	JiraDescriptionIssue,
	JiraDescriptionValue,
	ScreenshotBoundingBox
} from '@evinced-private/ui-common';

import { Report } from 'src/common/types/ReportModel';

/**
 * Render a structured {@link JiraDescriptionIssue} into the markdown string the Azure DevOps
 * ticket-creator module expects (it renders markdown to HTML for ADO). Mirrors the long-standing
 * Jira description layout so an exported Azure work item reads the same as the Jira one.
 *
 * A plain string description is passed through unchanged (with `note` appended when present).
 */
const buildAzureDescription = (description: JiraDescriptionValue, note?: string): string => {
	if (typeof description === 'string') {
		const base = description ?? '';
		return note ? `${base}\n\n*Notes:*\n ${note} \n\n` : base;
	}
	if (!description) {
		return '';
	}

	const issue: JiraDescriptionIssue = { ...description, note: note ?? description.note };
	let desc = '';
	desc += issue.pageUrl ? `*Url:* ${issue.pageUrl} \n\n` : '';
	desc += issue.type?.name ? `*Type:* ${issue.type.name} \n\n` : '';
	desc += issue.summary ? `*Summary:* ${issue.summary} \n\n` : '';
	desc += issue.description
		? `${issue.description
				.replace('**Description:**', '*Description:*')
				.replace('**Cause:**', '*Cause:*')
				.replace('**Effect:**', '*Effect:*')
				.replace('**Suggested Solutions:**', '*Suggested Solution:*')
				.replace('**Failure Summary:**', '*Failure Summary:*')} \n\n`
		: '';
	desc += issue.note ? `*Notes:*\n ${issue.note} \n\n` : '';

	const elements = issue.elements ?? [];
	if (elements.length > 1) {
		desc += 'h3. Elements: \n\n';
	}
	elements.forEach((element, i) => {
		desc += `*Selector ${i > 0 ? i + 1 : ''}:*\n{noformat}\n ${element.selector}\n{noformat} \n\n`;
		desc += `*HTML Snippet:*\n{code:html} ${element.domSnippet}\n{code} \n\n`;
	});

	desc += issue.screenshotUrl ? 'See attached screenshot. \n\n' : '';
	desc +=
		issue.tags?.length > 0
			? `*WCAG Violations:* ${issue.tags
					.filter((tag) => tag.description && tag.link)
					.map((tag) => `\n\n * [${tag.description}|${tag.link}]`)} \n\n`
			: '\n\n';

	return desc;
};

/** Normalize an element bounding box onto the `[x, y, width, height]` tuple the module expects. */
const toBoundingBoxTuple = (boundingBox: ScreenshotBoundingBox | number[]): number[] => {
	if (Array.isArray(boundingBox)) {
		return boundingBox;
	}
	const { x, y, width, height } = boundingBox;
	return [x, y, width, height];
};

/**
 * Map Platform's {@link Report} onto the azure-ticket-creator module's host-agnostic input
 * contract ({@link AzureTicketDraft}), so the module never depends on any Platform type.
 */
export const buildAzureTicketDraft = (issue: Report, note?: string): AzureTicketDraft => {
	const pageScreenshot = issue.screenshotDetails?.pageScreenshotUrl ?? issue.pageScreenshot;
	return {
		summary: issue.summary,
		description: buildAzureDescription(issue.description, note),
		pageScreenshot,
		elements: (issue.elements ?? []).map((element) => ({
			boundingBox: toBoundingBoxTuple(element.boundingBox)
		}))
	};
};
